/**
 * Autosave for one open editor. Framework-free so its guarantees are unit
 * tested (T07):
 * - debounced checkpoints, never two saves in flight at once;
 * - each save carries the last confirmed revision as its base, so a stale
 *   save becomes a server-side conflict instead of a silent overwrite;
 * - a response that arrives after newer typing never discards that typing;
 * - conflicts stop autosave until the person chooses, keeping the draft;
 * - retries reuse the idempotency key of an unchanged body, so a lost
 *   response cannot create a second revision.
 */

export type SaveResult = { status: 'saved'; revisionId: string } | { status: 'conflict'; currentRevisionId: string };

/** A failed save. Retryable failures (network, 5xx) are retried with backoff. */
export class SaveFailure extends Error {
  readonly retryable: boolean;
  constructor(message: string, retryable: boolean) {
    super(message);
    this.name = 'SaveFailure';
    this.retryable = retryable;
  }
}

export type AutosaveState =
  | { status: 'saved'; revisionId: string }
  | { status: 'pending' }
  | { status: 'saving' }
  | { status: 'error'; message: string; retrying: boolean }
  | { status: 'conflict'; currentRevisionId: string };

export type DraftStore<D> = { write(baseRevisionId: string, doc: D): void; clear(): void };

export type AutosaveOptions<D> = {
  baseRevisionId: string;
  save(baseRevisionId: string, doc: D, idempotencyKey: string): Promise<SaveResult>;
  debounceMs?: number;
  retryDelaysMs?: number[];
  onChange?(state: AutosaveState): void;
  draft?: DraftStore<D>;
  newKey?: () => string;
};

export class AutosaveController<D> {
  private readonly options: Required<Omit<AutosaveOptions<D>, 'draft' | 'onChange'>> & Pick<AutosaveOptions<D>, 'draft' | 'onChange'>;
  private base: string;
  private latest: D | undefined;
  private version = 0;
  private savedVersion = 0;
  private current: AutosaveState;
  private inFlight: Promise<void> | null = null;
  private runAgain = false;
  private debounce: ReturnType<typeof setTimeout> | undefined;
  private retryTimer: ReturnType<typeof setTimeout> | undefined;
  private retryAttempt = 0;
  /**
   * A request whose outcome is unknown (its response was lost). It is re-sent
   * unchanged, with the same idempotency key, before anything newer, so the
   * server can replay what happened instead of reporting a self-conflict.
   */
  private unconfirmed: { version: number; base: string; key: string; doc: D } | undefined;
  /** Revisions this editor has saved past; a late copy of one must never be loaded back. */
  private readonly superseded = new Set<string>();
  private disposed = false;

  constructor(options: AutosaveOptions<D>) {
    this.options = {
      debounceMs: 800,
      retryDelaysMs: [2000, 5000, 10_000, 30_000],
      newKey: () => crypto.randomUUID(),
      ...options,
    };
    this.base = options.baseRevisionId;
    this.current = { status: 'saved', revisionId: this.base };
  }

  get state(): AutosaveState {
    return this.current;
  }

  get baseRevisionId(): string {
    return this.base;
  }

  get hasUnsavedChanges(): boolean {
    return this.version !== this.savedVersion;
  }

  /** Records a local change: keeps a recoverable draft and schedules a checkpoint. */
  edit(doc: D): void {
    this.latest = doc;
    this.version += 1;
    this.options.draft?.write(this.base, doc);
    if (this.current.status === 'conflict') return; // wait for the person's choice
    if (this.current.status !== 'saving') this.setState({ status: 'pending' });
    this.schedule(this.options.debounceMs);
  }

  /** Saves now (e.g. before navigation) and resolves when nothing is in flight. */
  async flush(): Promise<void> {
    clearTimeout(this.debounce);
    clearTimeout(this.retryTimer);
    await this.run();
    while (this.inFlight) await this.inFlight;
  }

  /** Retries a failed save on request. */
  retry(): void {
    clearTimeout(this.retryTimer);
    void this.run();
  }

  /**
   * A revision was committed elsewhere. Returns true only if the caller should
   * load it into the editor: it is not the revision already held, not one this
   * editor saved past, and nothing local is unsaved (local changes are kept and
   * their next save surfaces a conflict instead of overwriting).
   */
  acceptRemote(revisionId: string): boolean {
    if (revisionId === this.base) return false;
    if (this.superseded.has(revisionId)) return false;
    if (this.hasUnsavedChanges || this.inFlight || this.current.status === 'conflict') return false;
    this.base = revisionId;
    this.setState({ status: 'saved', revisionId });
    return true;
  }

  /** Conflict resolution: deliberately save the local version on top of the current revision. */
  keepMine(currentRevisionId: string): void {
    this.unconfirmed = undefined;
    this.base = currentRevisionId;
    if (this.latest !== undefined) this.options.draft?.write(this.base, this.latest);
    this.setState({ status: 'pending' });
    void this.run();
  }

  /** Conflict resolution: drop local changes and adopt the saved revision. */
  discardMine(revisionId: string): void {
    clearTimeout(this.debounce);
    clearTimeout(this.retryTimer);
    this.unconfirmed = undefined;
    this.base = revisionId;
    this.savedVersion = this.version;
    this.latest = undefined;
    this.options.draft?.clear();
    this.setState({ status: 'saved', revisionId });
  }

  dispose(): void {
    this.disposed = true;
    clearTimeout(this.debounce);
    clearTimeout(this.retryTimer);
  }

  private schedule(ms: number): void {
    clearTimeout(this.debounce);
    this.debounce = setTimeout(() => void this.run(), ms);
  }

  private setState(state: AutosaveState): void {
    this.current = state;
    if (!this.disposed) this.options.onChange?.(state);
  }

  private run(): Promise<void> {
    if (this.inFlight) {
      this.runAgain = true;
      return this.inFlight;
    }
    if (!this.hasUnsavedChanges || this.latest === undefined || this.current.status === 'conflict') {
      return Promise.resolve();
    }
    const request = this.unconfirmed ?? { version: this.version, base: this.base, key: this.options.newKey(), doc: this.latest };
    this.setState({ status: 'saving' });

    this.inFlight = this.options
      .save(request.base, request.doc, request.key)
      .then((result) => {
        this.retryAttempt = 0;
        this.unconfirmed = undefined;
        if (result.status === 'conflict') {
          this.setState({ status: 'conflict', currentRevisionId: result.currentRevisionId });
          return;
        }
        if (result.revisionId !== this.base) this.superseded.add(this.base);
        this.base = result.revisionId;
        this.savedVersion = request.version;
        if (this.hasUnsavedChanges) {
          // Newer typing arrived while saving: keep it and save it next on the new base.
          if (this.latest !== undefined) this.options.draft?.write(this.base, this.latest);
          this.setState({ status: 'pending' });
          this.runAgain = true;
        } else {
          this.options.draft?.clear();
          this.setState({ status: 'saved', revisionId: result.revisionId });
        }
      })
      .catch((error: unknown) => {
        const failure = error instanceof SaveFailure ? error : new SaveFailure('Could not reach the server.', true);
        const delays = this.options.retryDelaysMs;
        const retrying = failure.retryable && delays.length > 0;
        // Outcome unknown: keep this exact request to confirm before saving anything newer.
        this.unconfirmed = failure.retryable ? request : undefined;
        this.setState({ status: 'error', message: failure.message, retrying });
        if (retrying) {
          const delay = delays[Math.min(this.retryAttempt, delays.length - 1)]!;
          this.retryAttempt += 1;
          clearTimeout(this.retryTimer);
          this.retryTimer = setTimeout(() => void this.run(), delay);
        }
      })
      .finally(() => {
        this.inFlight = null;
        if (this.runAgain && !this.disposed) {
          this.runAgain = false;
          if (this.current.status !== 'conflict' && this.current.status !== 'error') void this.run();
        }
      });
    return this.inFlight;
  }
}
